import Axios, { CreateAxiosDefaults } from 'axios';
import { store } from '../Store';
import { getAuthorizedUrl } from '../requests/auth.request';
import toast from 'react-hot-toast';
import { showToastMessage } from '../helpers/utils';
import { refreshSession } from '../requests/session.request';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Skip the "Request successfully processed" toast, for background fetches. */
    silent?: boolean;
    /** Set internally after a 401 so a request is retried at most once with a refreshed token. */
    _authRetried?: boolean;
  }
}

const options: CreateAxiosDefaults = {
  baseURL: window._env_.REACT_APP_WEF_API_BASEURL,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json'
  }
};

const api = Axios.create(options);

api.interceptors.request.use(
  (config) => {
    // Do something before request is sent
    const token = store.getState().config.accessToken;
    config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  async (response) => {
    if (response?.data?.errorType === 1) {
      //redirect to freshbooks
      const data = await getAuthorizedUrl();
      window.open(data.data, '_blank');
    }
    if (response?.data?.errors?.length > 0) {
      const formattedErrors = response?.data?.errors?.join('\n');
      showToastMessage('error', 'An error occurred', formattedErrors);
    } else if (response?.data?.success === true && !response.config?.silent) {
      toast.success('Request successfully processed');
    }
    return response;
  },
  async (error) => {
    // Expired access token: refresh once and replay the request.
    if (error?.response?.status === 401 && error.config && !error.config._authRetried) {
      const newToken = await refreshSession();
      if (newToken) {
        error.config._authRetried = true;
        error.config.headers = { ...error.config.headers, Authorization: `Bearer ${newToken}` };
        return api.request(error.config);
      }
      // refreshSession cleared the session; AuthProvider redirects to /login.
      return Promise.reject(error);
    }
    if (error.response.data?.errorType === 1) {
      //redirect to freshbooks
      const data = await getAuthorizedUrl();
      window.open(data.data, '_blank');
    }
    if (error.response.data?.errors?.length > 0) {
      const formattedErrors = error.response.data?.errors?.join('\n');
      showToastMessage('error', 'An error occurred', formattedErrors);
    } else if (error?.response?.status === 403) {
      toast.error('You do not have permission to access this.');
    } else if (error?.response?.status === 401) {
      // Still unauthorized after a refresh: nothing more to do here.
    } else {
      showToastMessage('error', 'An error occurred', 'Request not processed successfully.');
    }
    return Promise.reject(error);
  }
);

export default api;
